import React, { useMemo, useState } from 'react'
import { useStyles } from './MarketplaceMappingManagerTemplate.styles'
import {
  ButtonV2,
  CardsContainer,
  ModalConfirmV2,
  ModalSizeVariants,
  OfferCard,
  StatusChipBg,
  StatusChipText,
  StatusChipV3,
  TextInputV2,
  VerticalDivider,
} from '@labrav/react-components'
import {
  BuyerDetailRow,
  BuyerInfo,
} from '../../../common/components/DetailRow/DetailRow'
import { DateTime } from 'luxon'
import { CRMTypes, standardLabraDateFormat } from '../../../utils/constants'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import { faClone, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from '../../../store'
import { useNavigate } from 'react-router-dom'
import { useUserType } from '@labrav/common'
import { customerFormData } from '../utils/customerFormData'
import { MarketplaceTemplatesDetailsData } from '../../../modules/marketplaceMappingManager/types'
import { isEmpty } from 'lodash'
import {
  cloneMarketplaceReferralTemplate,
  deleteMarketplaceReferralTemplate,
  getMarketplaceReferralTemplatesInfo,
} from '../../../modules/marketplaceMappingManager/action'
import { updateAppAlert } from '@labrav/common'
import { MappingManagerTemplateStatusType } from '../../../types/external'
import { Grid } from '@mui/material'
import { CloudType } from '../../../types/external'
import { useFlagValue } from '@labrav/flags'
import { ShortenedNameV2 } from '@labrav/common'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

export const MarketplaceMappingManagerTemplate: React.FC<{
  cloudType: CloudType
  accountId?: string
}> = ({ cloudType, accountId }) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const partnerType = useUserType()
  const { timeZone } = useTimeZone()
  const { value: isSalesforceMappingManagerEnabled } = useFlagValue(
    'mappingManagerMultipleApnSalesforce'
  )
  const customerData = customerFormData()
  const { crmType } = customerData
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const templatesData = useSelector(
    state => state.marketplaceMappingManager.templateDetails
  )
  const partnerUserId = useSelector<string | undefined>(
    state => state.userProfile.userProfile.id
  ) as string

  const isCloneTemplateLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.CLONE_REFERRAL_TEMPLATE)
  )
  const isDeleteTemplateLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.DELETE_REFERRAL_TEMPLATE)
  )
  const shouldUseSalesforceLogic = useMemo(
    () =>
      isSalesforceMappingManagerEnabled &&
      crmType === CRMTypes.salesforce &&
      accountId,
    [isSalesforceMappingManagerEnabled, crmType, accountId]
  )
  const usesScopedTemplate =
    !!shouldUseSalesforceLogic ||
    (crmType === CRMTypes.hubspot && cloudType === CloudType.AZURE)
  const handleTemplateNameClick = (templateId?: string) => {
    if (!templateId) return
    if (usesScopedTemplate && accountId) {
      navigate(
        `/settings/${cloudType.toLowerCase()}/${accountId}/mappings/marketplace/templates/${templateId}`
      )
    } else {
      navigate(`/settings/mappings/marketplace/templates/${templateId}`)
    }
  }

  const [openCloneModal, setOpenCloneModal] = useState<boolean>(false)
  const [templateToClone, setTemplateToClone] =
    useState<null | MarketplaceTemplatesDetailsData>(null)
  const [cloneTemplateName, setCloneTemplateName] = useState('')
  const [cloneNameError, setCloneNameError] = useState('')
  const [acceptDisabled, setAcceptDisabled] = useState(false)

  const handleCloseCloneModal = () => {
    setOpenCloneModal(false)
    setTemplateToClone(null)
    setCloneTemplateName('')
    setCloneNameError('')
  }

  const handleCloneButtonClick = (
    template: MarketplaceTemplatesDetailsData
  ) => {
    setTemplateToClone(template)
    setCloneTemplateName(`Clone_${template.templateName}`)
    setOpenCloneModal(true)
  }

  const handleCloneNameBlur = () => {
    if (!cloneTemplateName.trim()) {
      setCloneNameError('Template name is required.')
      setAcceptDisabled(true)
    }
  }

  const handleCloneNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCloneTemplateName(value)
    if (!value.trim()) {
      setCloneNameError('Template name is required.')
      setAcceptDisabled(true)
    } else {
      setCloneNameError('')
      setAcceptDisabled(false)
    }
  }

  const handleCloneTemplate = async () => {
    const { templateId, errorMessage } = (await dispatch(
      cloneMarketplaceReferralTemplate(
        {
          name: cloneTemplateName,
          ...(!usesScopedTemplate ? { templateType: 'flyout' } : {}),
          ...(usesScopedTemplate
            ? { templateId: templateToClone?.templateId || '' }
            : {}),
          createdBy: partnerUserId,
        },
        crmId,
        templateToClone?.templateId || '',
        accountId,
        crmType as CRMTypes,
        usesScopedTemplate
      )
    )) as unknown as { templateId: string; errorMessage: string }
    if (!isEmpty(templateId)) {
      setOpenCloneModal(false)
      if (usesScopedTemplate && accountId && cloudType) {
        navigate(
          `/settings/${cloudType.toLowerCase()}/${accountId}/mappings/marketplace/templates/${templateId}`
        )
      } else {
        navigate(`/settings/mappings/marketplace/templates/${templateId}`)
      }
      dispatch(
        updateAppAlert({
          message: `${templateToClone?.templateName} cloned as ${cloneTemplateName} successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } else {
      const duplicateError = 'Template name already exists for this CRM'
      setCloneNameError(
        errorMessage && errorMessage.includes(duplicateError)
          ? 'Template already exists. Please enter a unique name.'
          : errorMessage || 'Failed to clone template. Please try again.'
      )
      setOpenCloneModal(true)
    }
  }

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [templateToDelete, setTemplateToDelete] =
    useState<null | MarketplaceTemplatesDetailsData>(null)

  const handleCloseDeleteModal = () => {
    setTemplateToDelete(null)
    setOpenDeleteModal(false)
  }
  const handleDeleteButtonClick = (
    template: MarketplaceTemplatesDetailsData
  ) => {
    setTemplateToDelete(template)
    setOpenDeleteModal(true)
  }
  const handleDeleteTemplate = async () => {
    await dispatch(
      deleteMarketplaceReferralTemplate(
        crmId,
        templateToDelete?.templateId || '',
        templateToDelete?.templateName || '',
        accountId,
        crmType as CRMTypes,
        usesScopedTemplate
      )
    )
    setOpenDeleteModal(false)
    await dispatch(
      getMarketplaceReferralTemplatesInfo(
        crmId,
        usesScopedTemplate ? 'marketplace' : 'flyout',
        crmType as string,
        accountId,
        undefined,
        undefined,
        cloudType
      )
    )
  }

  return (
    <Grid
      container
      className={classes.root}
      data-testid="marketplace-templates-container"
    >
      {templatesData?.map((template, index) => (
        <CardsContainer
          key={template.templateId || index}
          hoverEffect={true}
          handleClick={() =>
            handleTemplateNameClick(template?.templateId || '')
          }
        >
          <OfferCard
            name={
              <div className={classes.nameContainer}>
                <a
                  className={classes.templateName}
                  onClick={e => {
                    e.stopPropagation()
                    handleTemplateNameClick(template?.templateId || '')
                  }}
                >
                  <ShortenedNameV2
                    name={template.templateName}
                    length={30}
                    showClipboardTooltip={true}
                  />
                </a>
                {usesScopedTemplate && (
                    <>
                      <VerticalDivider height="24px" />
                      <div>
                        <span className={classes.templateType}>
                          {template.templateType === 'private_offer'
                            ? 'Offer'
                            : 'Reseller authorization'}
                        </span>
                      </div>
                    </>
                  )}

                <VerticalDivider height="24px" />

                <div>
                  <StatusChipV3
                    bgColor={
                      template.status === MappingManagerTemplateStatusType.DRAFT
                        ? StatusChipBg.GRAY
                        : StatusChipBg.GREEN
                    }
                    textColor={
                      template.status === MappingManagerTemplateStatusType.DRAFT
                        ? StatusChipText.GRAY
                        : StatusChipText.GREEN
                    }
                    label={
                      template.status === MappingManagerTemplateStatusType.DRAFT
                        ? 'Saved as draft'
                        : 'Active'
                    }
                  />
                </div>
              </div>
            }
            isLastCard={true}
            customiseForBuyer={true}
            showRightSectionOnHover
            rightSideSection={
              <div
                className={classes.rightSection}
                onClick={e => e.stopPropagation()}
              >
                <ButtonV2
                  styleType="link"
                  data-testid={'clone-marketplace-template-btn'}
                  smallVariant
                  onClick={() => handleCloneButtonClick(template)}
                >
                  <FontAwesomeIcon icon={faClone} className={classes.icon} />
                  Clone
                </ButtonV2>
                <VerticalDivider height="24px" />
                <ButtonV2
                  styleType="link"
                  data-testid={'delete-marketplace-template-btn'}
                  smallVariant
                  onClick={() => handleDeleteButtonClick(template)}
                >
                  <FontAwesomeIcon icon={faTrash} className={classes.icon} />
                  Delete
                </ButtonV2>
              </div>
            }
            hideLogo={true}
          />
          <BuyerDetailRow>
            <>
              <BuyerInfo
                title={'Created by'}
                value={
                  <div className={classes.templateDetailsText}>
                    <span>{template.createdBy || '-'}</span>
                  </div>
                }
                gridSize={4}
              />
              <BuyerInfo
                title={'Creation date'}
                value={
                  <div>
                    <span className={classes.templateDetailsText}>
                      {DateTime.fromISO(template.creationDate, {
                        zone: timeZone,
                      }).toFormat(standardLabraDateFormat)}
                    </span>
                  </div>
                }
                gridSize={4}
              />
              <BuyerInfo
                title={'Last updated'}
                value={
                  <div>
                    <span className={classes.templateDetailsText}>
                      {template.lastUpdatedAt === 'None'
                        ? '-'
                        : DateTime.fromISO(template.lastUpdatedAt, {
                            zone: timeZone,
                          }).toFormat(standardLabraDateFormat)}
                    </span>
                  </div>
                }
                showDivider={false}
                gridSize={4}
              />
            </>
          </BuyerDetailRow>
        </CardsContainer>
      ))}
      <ModalConfirmV2
        title="Clone template"
        icon={
          <FontAwesomeIcon
            icon={faClone}
            data-testid="delete-template-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseCloneModal}
        onDecline={handleCloseCloneModal}
        content={
          <div className={classes.cloneModalContentContainer}>
            <div className={classes.heading}>
              Are you sure you want to clone this template?
            </div>
            <div>
              <TextInputV2
                keyName="marketplace-clone-template"
                title="Enter a unique template name"
                placeHolder=""
                type="text"
                value={cloneTemplateName}
                onChange={handleCloneNameChange}
                onBlur={handleCloneNameBlur}
                isError={!!cloneNameError}
                helperText={!!cloneNameError}
                showSpinner={false}
                errorText={cloneNameError}
                isReadOnly={false}
                isRequired={true}
              />
            </div>
          </div>
        }
        open={openCloneModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Clone'}
        onAccept={handleCloneTemplate}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptDisabled={acceptDisabled}
        acceptLoading={isCloneTemplateLoading}
      />
      <ModalConfirmV2
        title="Delete template"
        icon={
          <FontAwesomeIcon
            icon={faTrash}
            data-testid="delete-template-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseDeleteModal}
        onDecline={handleCloseDeleteModal}
        content={
          <div className={classes.heading}>
            Are you sure you want to delete this template and the associated
            mappings?
          </div>
        }
        open={openDeleteModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Delete'}
        onAccept={handleDeleteTemplate}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptLoading={isDeleteTemplateLoading}
      />
    </Grid>
  )
}
